import { Drawer, Form } from 'antd';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/shallow';

import { ModernHotelContext } from 'app/context/HotelContext/ModernHotelContext';
import { useValidateRoomBulkEdit } from 'app/hooks/data/core/extranet/room/useRoomBulkEdit';
import { DAYS_OPTIONS } from 'app/typings/core/extranet/constants/days';
import type {
  BulkEditFormValues,
  BulkEditInitialValues,
  BulkEditPayload,
} from 'app/typings/core/extranet/rooms/roomBulkEdit';
import { STANDARD_DATE_FORMAT } from 'app/utils/dates';
import type { Interval } from 'app/utils/dayjs';
import { eachDayOfInterval, format } from 'app/utils/dayjs';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { inventoryStore } from '../../../../InventoryStore';
import { deriveBulkEditFields } from '../../../../utils/editedFields';
import { ItemPreview } from '../../../Common/ItemPreview/ItemPreview';
import { ValidationRoomBulkEditModal } from '../ValidationRoomBulkEditModal/ValidationRoomBulkEditModal';

import styles from './RoomBulkEdit.module.scss';
import { DatesSelector } from './_components/DatesSelector';
import { RoomBulkEditFooter } from './_components/RoomBulkEditFooter';
import { RoomBulkEditValues } from './_components/RoomBulkEditValues';

export const RULES = [{ required: true, message: 'Required' }];

export type FieldsDisplayValues = {
  stock: boolean;
  price: boolean;
  discount: boolean;
  discountPrice: boolean;
};

const isValueKey = (key: string): key is keyof FieldsDisplayValues => {
  return ['stock', 'price', 'discount', 'discountPrice'].includes(key);
};

type Props = {
  initialValues: BulkEditInitialValues;
  currentRange?: Interval;
  hotelId: number;
  autoDiscountRate?: number;
};

export const RoomBulkEdit = ({
  initialValues,
  currentRange,
  hotelId,
  autoDiscountRate,
}: Props) => {
  const { getRoom } = useContext(ModernHotelContext);
  const [form] = Form.useForm();

  const { setIsBulkEditOpen, isBulkEditDrawerOpen } = useStore(
    inventoryStore,
    useShallow((state) => ({
      isBulkEditDrawerOpen: state.roomBulkEditDrawer,
      setIsBulkEditOpen: state.setRoomBulkEditDrawer,
    }))
  );

  const room = getRoom(isBulkEditDrawerOpen.roomId);

  const initialDisplayValues: FieldsDisplayValues = useMemo(
    () => ({
      stock: false,
      price: false,
      discount: false,
      discountPrice: false,
    }),
    []
  );

  const formValues = Form.useWatch<BulkEditFormValues>([], form);

  const {
    data,
    mutateAsync: validateRoomBulkEdit,
    isLoading,
  } = useValidateRoomBulkEdit(hotelId);

  useEffect(() => form.setFieldsValue(initialValues), [form, initialValues]);

  const [isValidationBulkEditModalOpen, setIsValidationBulkEditModalOpen] =
    useState<boolean>(false);

  const [payload, setPayload] = useState<BulkEditPayload>({
    roomId: room?.id ?? 0,
    startDate: format(
      initialValues?.startDate ?? new Date(),
      STANDARD_DATE_FORMAT
    ),
    endDate: format(initialValues?.endDate ?? new Date(), STANDARD_DATE_FORMAT),
    days: initialValues.days ?? [],
    stock: initialValues.stock,
    price: initialValues.price,
    editedFields: [],
  });

  const [openValues, setOpenValues] =
    useState<FieldsDisplayValues>(initialDisplayValues);

  const onFinish = async (values: BulkEditFormValues) => {
    if (!room?.id) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
    const payload: BulkEditPayload = {
      roomId: room?.id,
      startDate: format(values.startDate, STANDARD_DATE_FORMAT),
      endDate: format(values.endDate, STANDARD_DATE_FORMAT),
      days: values.days,
      stock: values.stock,
      price: values.price,
      discount:
        values.discount !== undefined ? values.discount / 100 : undefined,
      discountPrice: values.discountPrice,
      editedFields: [],
    };

    payload.editedFields = deriveBulkEditFields(payload);

    setPayload(payload);

    const amplitudeValue = {
      from: format(values.startDate, STANDARD_DATE_FORMAT),
      to: format(values.endDate, STANDARD_DATE_FORMAT),
      daysChanged: DAYS_OPTIONS.filter((day) =>
        values.days.some((daySelect: number) => day.value === daySelect)
      ).map((day) => day.fullName),
      numberDaysChanged: eachDayOfInterval({
        start: values.startDate,
        end: values.endDate,
      }).filter((x) =>
        values.days.some(
          (daySelect: number) =>
            new Date(x).getDay() === (daySelect === 7 ? 0 : daySelect)
        )
      ).length,
      stock: values.stock || null,
      price: values.price || null,
      discount: values.discount !== undefined ? values.discount / 100 : null,
      discountPrice: values.discountPrice || null,
      isLegacy: false,
    };

    amplitudeTrack('Save Bulk Edit', amplitudeValue);

    await validateRoomBulkEdit(payload);
    setIsValidationBulkEditModalOpen(true);
  };

  const toggleSwitch = (
    switchValue: boolean,
    section: keyof FieldsDisplayValues
  ) => {
    if (isValueKey(section)) {
      setOpenValues((values) => ({ ...values, [section]: switchValue }));
      !switchValue && form.setFieldValue([section], undefined);
    }

    ['stock', 'price', 'discount', 'discountPrice'].forEach((field) => {
      form.validateFields([field]);
    });
  };

  const handleClose = () => {
    setIsBulkEditOpen(undefined, false);
    form.resetFields();
    setOpenValues(initialDisplayValues);
    setIsValidationBulkEditModalOpen(false);
  };

  return (
    <Drawer
      title={
        <div className={styles.bulkEditHeader}>
          <h1>Bulk Edit</h1>
        </div>
      }
      className={styles.bulkEdit}
      width="510px"
      closable={false}
      open={isBulkEditDrawerOpen.isOpened}
      bodyStyle={{ padding: 0 }}
      onClose={handleClose}
      destroyOnClose
    >
      <Form
        name="bulkEditForm"
        autoComplete="off"
        className={styles.form}
        form={form}
        onFinish={onFinish}
        initialValues={initialValues}
      >
        <div className={styles.bulkEditBody}>
          <ItemPreview
            title="ROOM"
            name={room?.name ?? room?.categoryName ?? 'Unnamed Room'}
            images={room?.coverPicture ? [room.coverPicture] : undefined}
          />
          <DatesSelector
            fromDate={formValues?.startDate}
            days={initialValues.days}
            startDate={initialValues.startDate}
            endDate={initialValues.endDate}
          />
          <RoomBulkEditValues
            initialValues={initialValues}
            openValues={openValues}
            toggleSwitch={toggleSwitch}
          />
        </div>
        <RoomBulkEditFooter
          handleClose={handleClose}
          form={form}
          isLoading={isLoading}
          openValues={openValues}
        />
      </Form>
      {!isLoading && data && currentRange && (
        <ValidationRoomBulkEditModal
          isOpened={isValidationBulkEditModalOpen}
          setIsValidationBulkEditModalOpen={setIsValidationBulkEditModalOpen}
          hotelId={hotelId}
          payload={payload}
          openingDays={data}
          autoDiscountRate={autoDiscountRate}
          handleClose={handleClose}
        />
      )}
    </Drawer>
  );
};
